/**
 * shareLink.ts — T-500 authed deep-link builder + clipboard helper (pure, minimal DOM).
 *
 * The Share affordance (Noreen item 8, option A) produces an AUTHED deep-link to the location the
 * user is looking at. The link is just the app URL carrying the existing T-237 hash state
 * (#<zoom>/<lat>/<lng>&tract=…&pin=…), which encodes ONLY the location — the viewport, the selected
 * census tract's GEOID, and the site pin. It carries NO scores and NO personal data (guardrail 1):
 * MapHashState's type has no field for a score, so buildShareUrl CANNOT leak one, and the recipient's
 * scores are re-fetched and rendered only AFTER they authenticate.
 *
 * Because the app is router-less and this state rides in the URL *hash*, the link survives the
 * Cognito login wall unchanged — RequireAuth swaps LoginForm↔MapShell without navigating — so an
 * unauthenticated recipient lands on the shared location automatically once they sign in.
 */
import { encodeMapHash, parseMapHash, type MapHashState } from './urlHash'

/** The origin + path the link is built against — window.location fields, injected for testability. */
export interface ShareBase {
  origin: string
  pathname: string
}

/**
 * Compose the absolute, shareable deep-link for a location: `${origin}${pathname}${hash}`, where the
 * hash is the canonical T-237 encoding of the current viewport + selection. Location only — the
 * MapHashState type cannot carry a score or an address.
 */
export function buildShareUrl(base: ShareBase, state: MapHashState): string {
  return `${base.origin}${base.pathname}${encodeMapHash(state)}`
}

/**
 * True when `hash` encodes an actual shared LOCATION — a site pin or a selected tract — as opposed
 * to a bare viewport camera. RequireAuth uses this to tell an arriving recipient, on the login wall,
 * that a location was shared with them (T-500 funnel context), without depending on MapShell.
 */
export function hasSharedLocation(hash: string): boolean {
  const decoded = parseMapHash(hash)
  return !!(decoded && (decoded.pin || decoded.tract))
}

/**
 * Copy `text` to the clipboard, resolving true on success and false on failure (so the caller can
 * fall back to showing the link for manual copy). Prefers the async Clipboard API; falls back to a
 * hidden-textarea + execCommand for older/insecure contexts where navigator.clipboard is absent.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Clipboard API rejected (permissions / insecure context) — fall through to the legacy path.
  }
  try {
    if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'absolute'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
